import { Injectable, inject, signal } from '@angular/core'
import { Observable, catchError, first, firstValueFrom, from, map, of, tap, timeout } from 'rxjs'
import { Device } from '../models/Device'
import { AppStateService } from '../store/state/app/app.state.service'
import { DataService } from './data.service'
import { EnvironmentService } from './environment.service'
import { NotificationService } from './notification.service'

const GEO_PERMISSION_DENIED = 1

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  browser = signal('')
  locationDisabled = signal(false)
  locationDenied = signal(false)

  // Accept a geolocation fix once its accuracy (in metres) is at or under this value.
  readonly #accuracyThreshold = 100
  readonly #geoTimeout = 15000
  readonly #maxPositionAge = 60000

  #notificationService = inject(NotificationService)
  #appStateService = inject(AppStateService)
  #dataService = inject(DataService)
  #environmentService = inject(EnvironmentService)
  #logErrorUrl = this.#environmentService.getUrl('ng/v1/user/logerror/')

  async initDevice(skipLocationRequest: boolean = false) {
    this.locationDisabled.set(false)
    this.browser.set('')

    // If location should be skipped, use default 0,0 coordinates
    if (skipLocationRequest) {
      const device = this.#constructDeviceWithDefaultLocation()
      this.#appStateService.setDevice(device)
      return
    }

    const startedAt = performance.now()
    const { position, denied, error, best } = await firstValueFrom(this.#watchBestPosition())
    if (!position) {
      this.locationDisabled.set(true)
      this.locationDenied.set(denied)
      this.browser.set(this.getBrowserInfo().name)
      await this.#logLocationError(error, best, performance.now() - startedAt)
      return
    }
    const device = this.#constructDevice(position)
    this.#appStateService.setDevice(device)
  }

  #watchBestPosition(): Observable<{
    position: GeolocationPosition | null,
    denied: boolean,
    error: GeolocationPositionError | null,
    best: GeolocationPosition | null
  }> {
    let bestPosition: GeolocationPosition | null = null
    let denied = false
    let lastError: GeolocationPositionError | null = null

    const positions$ = new Observable<GeolocationPosition>((subscriber) => {
      const watchId = navigator.geolocation.watchPosition(
        (position) => subscriber.next(position),
        (error) => {
          lastError = error
          if (error.code === GEO_PERMISSION_DENIED) {
            denied = true
            subscriber.error(error)
          }
        },
        { enableHighAccuracy: true, maximumAge: this.#maxPositionAge }
      )
      return () => navigator.geolocation.clearWatch(watchId)
    })

    return positions$.pipe(
      tap((position) => {
        if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy) {
          bestPosition = position
        }
      }),
      first((position) => position.coords.accuracy <= this.#accuracyThreshold),
      timeout(this.#geoTimeout),
      map((position) => ({ position, denied: false, error: lastError, best: position })),
      catchError(() => of({
        position: denied ? null : bestPosition,
        denied,
        error: lastError,
        best: bestPosition
      }))
    )
  }

  async #logLocationError(
    error: GeolocationPositionError | null,
    best: GeolocationPosition | null,
    elapsedMs: number
  ): Promise<void> {
    try {
      if (error?.code === GEO_PERMISSION_DENIED) {
        return
      }
      const sessionId = this.#appStateService.getSession()?.SessionID
      if (!sessionId) {
        return
      }
      const message = await this.#buildLocationErrorLog(error, best, elapsedMs)
      const payload = JSON.stringify(message)
      this.#dataService.post<string, unknown>(this.#logErrorUrl + sessionId, payload, undefined, true)
        .subscribe({
          error: () => { }
        })
    } catch (loggingError) {
      console.warn('Failed to log location error', loggingError)
    }
  }

  async #buildLocationErrorLog(
    error: GeolocationPositionError | null,
    best: GeolocationPosition | null,
    elapsedMs: number
  ): Promise<string> {
    const deviceType = this.getDeviceType()
    const browserInfo = this.getBrowserInfo()
    const browserIsMobile = deviceType === 'Mobile' || deviceType === 'Tablet'
    return [
      `occurredAt=${new Date().toISOString()}`,
      `errorCode=${error?.code ?? 0}`,
      `errorMessage=${error?.message || `No position fix within ${this.#geoTimeout}ms`}`,
      `permissionState=${await this.#getGeolocationPermissionState()}`,
      `elapsedMs=${Math.round(elapsedMs)}`,
      `latitude=${best ? best.coords.latitude : 'none'}`,
      `longitude=${best ? best.coords.longitude : 'none'}`,
      `geoAccuracy=${best ? best.coords.accuracy : 'none'}`,
      `platform=${this.getPlatformInfo()}`,
      `deviceType=${deviceType}`,
      `browser=${browserInfo.name} ${browserInfo.version ?? 'Unknown'}`,
      `browserIsMobile=${browserIsMobile}`,
      `browserMobileModel=${browserIsMobile ? this.getMobileModel() : 'n/a'}`,
      `userAgent=${navigator.userAgent}`
    ].join('\n')
  }

  async #getGeolocationPermissionState(): Promise<string> {
    try {
      if (!navigator.permissions?.query) {
        return 'unsupported'
      }
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      return status.state
    } catch {
      return 'unknown'
    }
  }

  #constructDevice(position: GeolocationPosition): Device {
    const geoAccuracy = position.coords?.accuracy
    const userAgent = navigator.userAgent
    
    const platformInfo = this.getPlatformInfo()
    const deviceType = this.getDeviceType()
    const browserInfo = this.getBrowserInfo()
    const browserIsMobile = deviceType === 'Mobile' || deviceType === 'Tablet'
    const browserMobileModel = browserIsMobile ? this.getMobileModel() : ''
    return {
      platform: platformInfo,
      deviceType: deviceType,
      browserName: browserInfo.name,
      userAgent,
      browserIsMobile,
      browserMobileModel,
      geoAccuracy,
      latitude: position.coords.latitude.toString(),
      longitude: position.coords.longitude.toString()
    }
  }

  #constructDeviceWithDefaultLocation(): Device {
    const userAgent = navigator.userAgent
    
    const platformInfo = this.getPlatformInfo()
    const deviceType = this.getDeviceType()
    const browserInfo = this.getBrowserInfo()
    const browserIsMobile = deviceType === 'Mobile' || deviceType === 'Tablet'
    const browserMobileModel = browserIsMobile ? this.getMobileModel() : ''
    return {
      platform: platformInfo,
      deviceType: deviceType,
      browserName: browserInfo.name,
      userAgent,
      browserIsMobile,
      browserMobileModel,
      geoAccuracy: 0,
      latitude: '0',
      longitude: '0'
    }
  }

  getHashForDeviceId(uuid: string, email: string): Observable<string> {
    if (localStorage.getItem(email)) {
      return of(localStorage.getItem(email))
    }
    const encoder = new TextEncoder()
    const data = encoder.encode(uuid + email)
    return from(crypto.subtle.digest('SHA-256', data)).pipe(map((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      localStorage.setItem(email, hashHex)
      return hashHex
    }))
  }

  getPlatformInfo() {
    const ua = navigator.userAgent
    if (/Macintosh/.test(ua)) return 'Apple'
    if (/Windows/.test(ua)) return 'Windows'
    if (/Android/.test(ua)) return 'Android'
    if (/Linux/.test(ua)) return 'Linux'
    if (/iPhone|iPad|iPod/.test(ua)) return 'Apple'
    return 'Unknown'
  }

  getDeviceType(): 'Tablet' | 'Mobile' | 'Web' {
    const ua = navigator.userAgent
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'Tablet'
    }
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(ua)) {
      return 'Mobile'
    }
    return 'Web'
  }

  getMobileModel() {
    const userAgent = navigator.userAgent
    const modelRegex = /(Galaxy|iPhone|Pixel|tablet|ipad|playbook|silk|Mate|P|Find)/i;
  
    const modelMatch = userAgent.match(modelRegex);
  
    if (modelMatch) {
      return `${modelMatch[1]}`;
    }
    return ''
  }

  getBrowserInfo() {
    const userAgent = navigator.userAgent;

    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    // Check for common browsers
    if (userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg') > -1) {
        browserName = 'Edge';
        browserVersion = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('CriOS') > -1) {
        // Chrome on iOS
        browserName = 'Chrome (iOS)';
        browserVersion = userAgent.match(/CriOS\/(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('Chrome') > -1) {
        browserName = 'Chrome';
        browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
        browserName = 'Safari';
        browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('Firefox') > -1) {
        browserName = 'Firefox';
        browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) {
        browserName = 'IE';
        browserVersion =
            userAgent.match(/MSIE (\d+\.\d+)/)?.[1] || userAgent.match(/rv:(\d+\.\d+)/)?.[1];
    } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
        browserName = 'Opera';
        browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1];
    }
    return { name: browserName, version: browserVersion }
  }

}